export interface IStudentSubjectAccessEntry {
  schoolSubjectId: number;
}

export interface IStudentSubjectAssignments {
  studentId: number;
  subjectAccessEntries: readonly IStudentSubjectAccessEntry[];
}

export interface IStudentSubjectAccessGroup {
  studentIds: number[];
  subjectIds: number[];
}

/** StudentClassSubject entries are the source of truth; only an explicit assignment grants access. */
export function hasStudentAccessToSchoolSubject(
  subjectAccessEntries: readonly IStudentSubjectAccessEntry[] | null | undefined,
  schoolSubjectId: number | null,
): boolean {
  return subjectAccessEntries?.some((entry) => entry.schoolSubjectId === schoolSubjectId) ?? false;
}

/**
 * Partitions students into groups sharing the same set of allowed subjects, restricted to the given
 * accessible subjects. Only explicit assignments grant access (see hasStudentAccessToSchoolSubject), so
 * students without any matching entry are omitted; students not listed (e.g. removed from the class)
 * never appear.
 */
export function groupStudentsBySubjectAccess(
  students: readonly IStudentSubjectAssignments[],
  accessibleSubjectIds: readonly number[],
): IStudentSubjectAccessGroup[] {
  const accessibleSet = new Set(accessibleSubjectIds);
  const groupsByKey = new Map<string, IStudentSubjectAccessGroup>();

  for (const student of students) {
    const allowedSubjectIds = Array.from(new Set(student.subjectAccessEntries.map((entry) => entry.schoolSubjectId)))
      .filter((subjectId) => accessibleSet.has(subjectId))
      .sort((a, b) => a - b);

    if (allowedSubjectIds.length === 0) continue;

    const key = allowedSubjectIds.join(',');
    const group = groupsByKey.get(key);
    if (group) {
      group.studentIds.push(student.studentId);
    } else {
      groupsByKey.set(key, { studentIds: [student.studentId], subjectIds: allowedSubjectIds });
    }
  }

  return Array.from(groupsByKey.values());
}
